import { useHandler } from "@redotech/react-util/hook";
import { SignupFormLocation } from "@redotech/redo-marketing-common/schemas/submit-signup-form-step-schema";
import { MarketingEntryPoint } from "@redotech/redo-marketing-entry-point-common/marketing-entry-point";
import {
  SpotlightForm,
  SpotlightTriggerType,
} from "@redotech/redo-spotlight-common/spotlight-schema";
import { WidgetAutocheck } from "@redotech/redo-storefront-widget-common/widget";
import { SpotlightInstance } from "@redotech/redo-web/marketing/spotlight/spotlight-instance";
import {
  BackInStockContext,
  SpotlightClient,
} from "@redotech/redo-web/marketing/spotlight/types";
import { submitBackInStockForm } from "@redotech/shopify-sdk/rpc/schema/forms/submit-back-in-stock-form";
import { getCustomFieldOptions } from "@redotech/shopify-sdk/rpc/schema/marketing-embedded-form/get-custom-field-options";
import { getSpotlight } from "@redotech/shopify-sdk/rpc/schema/spotlight/get-spotlight";
import { logSpotlightAnalytics } from "@redotech/shopify-sdk/rpc/schema/spotlight/log-spotlight-analytics";
import { submitSpotlightStep } from "@redotech/shopify-sdk/rpc/schema/spotlight/submit-spotlight-step";
import {
  memo,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { RedoShopifyRpcClientContext } from "../client/redo-shopify-rpc-client";
import {
  getAllPdpAddProductForms,
  getSoldOutFallbackElements,
} from "../product-page";
import { useVariantStockStatus } from "../redo-storefront/marketing/product-back-in-stock/storefront";
import { isOnProductPage } from "../shopify";
import { getStorefrontLocale } from "../storefront-locale";
import { getRenderSafeDomObserver } from "../utils/dom";

/**
 * Discovers PDP /cart/add forms (or sold-out fallbacks) so embed launchers
 * on BACK_IN_STOCK spotlights can attach to them when no explicit
 * anchorCssSelector is configured.
 */
function usePdpAttachPoints(autocheck: WidgetAutocheck): Element[] {
  const [points, setPoints] = useState<Element[]>([]);

  const discover = useHandler(() => {
    const onTapcart = !!globalThis.Tapcart && !globalThis.Shopify;
    if (!isOnProductPage(onTapcart)) {
      setPoints((prev) => (prev.length > 0 ? [] : prev));
      return;
    }
    let targets = getAllPdpAddProductForms({
      onTapcart,
      autocheckSelector: autocheck.marketing.backInStockButtonSelector,
    });
    if (targets.length === 0 && autocheck.marketing.backInStockButtonSelector) {
      targets = getAllPdpAddProductForms({ onTapcart });
    }
    if (targets.length === 0) {
      targets = getSoldOutFallbackElements();
    }
    setPoints((prev) => {
      if (
        prev.length === targets.length &&
        prev.every((el, i) => el === targets[i])
      ) {
        return prev;
      }
      return targets;
    });
  });

  useEffect(() => {
    discover();
    const obs = getRenderSafeDomObserver();
    obs.registerCallback(discover);
    return () => obs.unregisterCallback(discover);
  }, [discover]);

  return points;
}

export const SpotlightInstanceWrapper = memo(function SpotlightInstanceWrapper({
  autocheck,
  forceId,
  forceSpotlight,
}: {
  autocheck: WidgetAutocheck;
  forceId?: string;
  forceSpotlight?: SpotlightForm;
}) {
  const brandKit = autocheck.brandKit;
  const rpcClient = useContext(RedoShopifyRpcClientContext);
  const shopifyCustomerId = useMemo(
    () =>
      (
        globalThis as any
      ).ShopifyAnalytics?.meta?.page?.customerId?.toString() ?? null,
    [],
  );

  const client: SpotlightClient | undefined = useMemo(() => {
    if (!rpcClient) {
      return;
    }
    return {
      getSpotlight: async (params, options) =>
        rpcClient(getSpotlight, params, options),
      getCustomFieldOptions: async (params) =>
        rpcClient(getCustomFieldOptions, params),
      logSpotlightAnalytics: async (params) =>
        rpcClient(logSpotlightAnalytics, params),
      submitSpotlightStep: async (params) =>
        rpcClient(submitSpotlightStep, params),
    } satisfies SpotlightClient;
  }, [rpcClient]);

  const location =
    globalThis.redoStorefront?.type === "commentsold"
      ? SignupFormLocation.COMMENTSOLD_STOREFRONT
      : SignupFormLocation.SHOPIFY_STOREFRONT;

  const { isOutOfStock, isCheckingStock, productId, variantId } =
    useVariantStockStatus();

  const backInStockContext: BackInStockContext | null = useMemo(() => {
    if (isCheckingStock || !isOutOfStock || !productId || !variantId) {
      return null;
    }
    return { productId, variantId };
  }, [isCheckingStock, isOutOfStock, productId, variantId]);

  const pdpAttachPoints = usePdpAttachPoints(autocheck);

  const handleBackInStockSubmit = useCallback(
    (context: BackInStockContext, formData: Record<string, string>) => {
      if (!rpcClient || !(formData.email || formData.phone)) {
        return;
      }
      rpcClient(submitBackInStockForm, {
        email: formData.email || undefined,
        phone: formData.phone || undefined,
        productId: context.productId,
        variantId: context.variantId,
      }).catch((e) =>
        console.error("Failed to submit back-in-stock subscription", e),
      );
    },
    [rpcClient],
  );

  const storefrontLocale = useMemo(() => getStorefrontLocale(), []);

  const commonProps = {
    brandKit,
    country: storefrontLocale.country,
    entryPoint: MarketingEntryPoint.SIGNUP_FORM,
    language: storefrontLocale.language,
    location,
    rpcClient: client,
    shopifyCustomerId,
  };

  if (forceId || forceSpotlight) {
    return (
      <SpotlightInstance
        {...commonProps}
        forceId={forceId}
        forceSpotlight={forceSpotlight}
        localStorageKey="redo.spotlight.preview"
        triggerType={SpotlightTriggerType.TIME_ON_PAGE}
      />
    );
  }

  return (
    <>
      <SpotlightInstance
        {...commonProps}
        localStorageKey="redo.spotlight.time_on_page"
        triggerType={SpotlightTriggerType.TIME_ON_PAGE}
      />
      <SpotlightInstance
        {...commonProps}
        localStorageKey="redo.spotlight.exit_intent"
        triggerType={SpotlightTriggerType.EXIT_INTENT}
      />
      <SpotlightInstance
        {...commonProps}
        backInStockContext={backInStockContext}
        isVariantOutOfStock={isOutOfStock && !isCheckingStock}
        localStorageKey="redo.spotlight.back_in_stock"
        onBackInStockSubmit={handleBackInStockSubmit}
        pdpAttachPoints={pdpAttachPoints}
        triggerType={SpotlightTriggerType.BACK_IN_STOCK}
      />
    </>
  );
});
